import { BridgeError, call, isNativeHost } from "@/bridge/bridge";
import { env, isDevBuild } from "@/config/env";

const EXCHANGE_DEBUG_KEY = "sgc.debug.exchange";

export type ExchangeSource = "boot" | "renew";

function recordExchangeAttempt(entry: Record<string, unknown>): void {
  if (!isDevBuild) return;
  try {
    const raw = window.localStorage.getItem(EXCHANGE_DEBUG_KEY);
    const trail: unknown[] = raw === null ? [] : JSON.parse(raw);
    trail.push({ at: new Date().toISOString(), ...entry });
    window.localStorage.setItem(
      EXCHANGE_DEBUG_KEY,
      JSON.stringify(trail.slice(-20)),
    );
  } catch {
    // Diagnostics must never break the flow they are diagnosing.
  }
}

/**
 * The web session lives in an HttpOnly cookie set by
 * POST /v1/webview/session/exchange. This module never sees it — JavaScript
 * cannot read the cookie, which is the point: a compromised page can act
 * inside the open session but cannot carry the credential away.
 *
 * All this module does is trade a one-time code for that cookie.
 */

export const WEBVIEW_SESSION_PATHS = {
  exchange: "/v1/webview/session/exchange",
  logout: "/v1/webview/session/logout",
} as const;

let inFlightExchange: Promise<boolean> | null = null;

/**
 * Exchanges a one-time code for the session cookie. Returns false when there
 * is no code to spend or the API rejects it, which the caller renders as "this
 * screen cannot be opened" rather than retrying forever.
 *
 * Concurrent callers share one round trip, so a burst of 401s cannot turn into
 * a burst of exchanges.
 */
export function exchangeSession(
  code: string,
  source: ExchangeSource = "boot",
): Promise<boolean> {
  inFlightExchange ??= postExchange(code, source);
  return inFlightExchange;
}

async function postExchange(
  code: string,
  source: ExchangeSource,
): Promise<boolean> {
  try {
    // Deliberately not routed through apiRequest: this call establishes the
    // session that apiRequest depends on, and it answers 204 with no envelope.
    const response = await fetch(
      `${env.apiBaseUrl}${WEBVIEW_SESSION_PATHS.exchange}`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-SGC-Webview": "1",
        },
        body: JSON.stringify({ code }),
      },
    );
    recordExchangeAttempt({
      source,
      url: `${env.apiBaseUrl}${WEBVIEW_SESSION_PATHS.exchange}`,
      status: response.status,
      ok: response.ok,
    });
    return response.ok;
  } catch (error) {
    recordExchangeAttempt({
      source,
      url: `${env.apiBaseUrl}${WEBVIEW_SESSION_PATHS.exchange}`,
      threw: error instanceof Error ? error.message : String(error),
    });
    return false;
  } finally {
    inFlightExchange = null;
  }
}

export async function openSession(code: string | null): Promise<boolean> {
  if (code === null) {
    return true;
  }

  if (await exchangeSession(code)) {
    return true;
  }

  console.warn("[session] the injected code was not accepted; renewing");
  return renewSession();
}

export async function renewSession(): Promise<boolean> {
  if (!isNativeHost()) return false;

  try {
    const { code } = await call("session.exchangeCode");
    if (code === null) return false;
    return await exchangeSession(code, "renew");
  } catch (error) {
    if (error instanceof BridgeError) return false;
    throw error;
  }
}

/**
 * Ends the session: drops it server-side, then lets the shell decide what
 * happens next (normally: close this WebView and show the PIN screen).
 */
export async function signOut(): Promise<void> {
  try {
    await fetch(
      `${env.apiBaseUrl}${WEBVIEW_SESSION_PATHS.logout}`,
      { method: "POST", credentials: "include", headers: { "X-SGC-Webview": "1" } },
    );
  } catch {
    // Best effort. The session expires on its own within minutes anyway.
  }

  if (isNativeHost()) {
    await call("session.signOut");
  }
}
